/**
 * Category ranking — where the spend leaks.
 * Editorial rank list with hairline meters and oldstyle numerals.
 */
import { store } from '../store.ts';
import { formatINR, CATEGORY_DISPLAY } from '../types.ts';
import type { Category } from '../types.ts';

export class HisaabCategoryRank extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.totals.by_category as Record<Category, number>, s.summary.totals.inr);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(byCat: Record<Category, number>, total: number): void {
    const entries = (Object.entries(byCat) as [Category, number][])
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    if (entries.length === 0) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }

    const rows = entries.map(([cat, val], i) => {
      const pct = total > 0 ? (val / total) * 100 : 0;
      return /* html */ `
<div class="row" style="--pct:${pct}%" tabindex="0">
  <div class="rank">${String(i + 1).padStart(2, '0')}</div>
  <div class="cat">
    <div class="name">${CATEGORY_DISPLAY[cat] ?? cat}</div>
    <div class="meter"><span></span></div>
  </div>
  <div class="amount">${formatINR(val)}</div>
</div>`;
    }).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
:host { display: block; }
.title {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-bottom: 14px;
  padding-left: 4px;
}
.list {
  display: grid;
  gap: 0;
  background: var(--paper-up);
  border: 1px solid var(--rule);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}
/* hairline ornament — saffron rule at top */
.list::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, var(--saffron) 0%, transparent 30%);
  opacity: 0.6;
  z-index: 1;
}
.row {
  display: grid;
  grid-template-columns: 38px 1fr auto;
  gap: 14px;
  align-items: center;
  padding: 15px 18px;
  border-bottom: 1px solid var(--rule);
  cursor: default;
  transition: background 240ms cubic-bezier(0.32, 0.72, 0, 1);
}
.row:last-child { border-bottom: none; }
.row:hover,
.row:focus-visible {
  background: color-mix(in oklch, var(--saffron) 8%, var(--paper-up));
  outline: none;
}
.rank {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--saffron);
  letter-spacing: 0.08em;
  font-feature-settings: 'tnum' 1;
}
.cat { display: grid; gap: 7px; min-width: 0; }
.name {
  font-family: var(--display);
  font-size: 19px;
  color: var(--ink);
  font-weight: 600;
  letter-spacing: -0.005em;
}
.meter {
  height: 2px;
  background: oklch(20% 0.04 280);
  border-radius: 999px;
  overflow: hidden;
}
.meter span {
  display: block;
  height: 100%;
  width: var(--pct);
  background: linear-gradient(90deg, var(--saffron-dim), var(--saffron));
  animation: grow 700ms cubic-bezier(0.32, 0.72, 0, 1) both;
}
.row:first-child .meter span {
  background: linear-gradient(90deg, var(--saffron-dim), var(--saffron-glow, var(--saffron)));
  box-shadow: 0 0 8px oklch(78% 0.16 65 / 0.4);
}
.amount {
  font-family: var(--display);
  font-size: 19px;
  font-weight: 600;
  color: var(--ink);
  font-feature-settings: 'tnum' 1, 'lnum' 1, 'ss01' 1;
  letter-spacing: -0.01em;
}
@keyframes grow {
  from { transform: scaleX(0); transform-origin: left; }
  to   { transform: scaleX(1); }
}
@media (prefers-reduced-motion: reduce) {
  .meter span { animation: none; }
}
</style>
<div class="title">Kahaan se nikla paisa</div>
<div class="list">${rows}</div>
`;
  }
}

customElements.define('hisaab-category-rank', HisaabCategoryRank);
